import React, { useRef } from 'react';
import { useDndContext, useDraggable } from '@dnd-kit/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { DndColumn, DndParentEntry } from '../types';
import { useSelectionContext } from '../contexts/SelectionContext';
import { SelectionType } from '../hooks/useSelectionState';
import DndChildCard from './DndChildCard';

// ─── Couleurs selon l'état de l'item ─────────────────────────────────────────

const COLORS_LIGHT = {
  flat: { default: '#f8fafc', active: '#e2e8f0', selected: '#c8d9ea', border: '#90afc5' },
  partial: { default: '#e3f2fd', active: '#bde2fdff', selected: '#c3e6ffff', border: '#2196f3' },
  full: {
    default: 'rgb(255, 243, 224)',
    active: 'rgba(255, 227, 182, 1)',
    selected: 'rgba(255, 221, 167, 1)',
    border: 'rgb(255, 152, 0)'
  }
};

const COLORS_DARK = {
  flat: { default: '#252838', active: '#2D3149', selected: '#2D4158', border: '#90afc5' },
  partial: {
    default: 'rgba(33,150,243,.12)',
    active: 'rgba(33,150,243,.2)',
    selected: 'rgba(33,150,243,.25)',
    border: '#2196f3'
  },
  full: {
    default: 'rgba(255,152,0,.12)',
    active: 'rgba(255,152,0,.2)',
    selected: 'rgba(255,152,0,.25)',
    border: 'rgb(255, 152, 0)'
  }
};

function getColors(isFlat: boolean, isFullParent: boolean, isDark: boolean) {
  const palette = isDark ? COLORS_DARK : COLORS_LIGHT;
  if (isFlat) return palette.flat;
  return isFullParent ? palette.full : palette.partial;
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const DndItemCard = ({
  item,
  columnId,
  isOpen,
  onToggleAccordion
}: {
  item: DndParentEntry;
  columnId: DndColumn['id'];
  isOpen: (columnId: string, itemCode: string) => boolean;
  onToggleAccordion: (itemCode: string) => void;
}) => {
  const { over, active } = useDndContext();
  const {
    isSelected,
    toggleSelect,
    removeMany,
    isMultiDragActive,
    selectionSize,
    isDark,
    isReadOnly
  } = useSelectionContext();

  const isFlat = item.children.length === 0 && (item.childrenLength ?? 0) === 0;
  const isExpanded = isFlat ? false : isOpen(columnId, item.label);
  const isItemSelected = isSelected(columnId, 'parent', item.id);
  const isDisabledDrag = selectionSize > 1 && !isItemSelected;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `item-${item.id}-column-${columnId}`,
    data: { type: 'parent', item_id: item.id, element: item, columnId },
    disabled: isDisabledDrag || isReadOnly
  });

  const accordionDetailsRef = useRef<HTMLDivElement>(null);

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  const isActive = over?.id === columnId && item.id === active?.data?.current?.item_id;
  const colors = getColors(isFlat, item.isFullParent, isDark);

  const handleCtrlClick = (e: React.MouseEvent) => {
    if (isReadOnly) return;
    if (e.ctrlKey || e.metaKey) {
      if (accordionDetailsRef.current?.contains(e.target as Node)) return;
      e.stopPropagation();
      if (!isItemSelected) {
        removeMany(
          item.children.map((c) => ({
            columnId,
            type: 'child' as SelectionType,
            itemId: c.id
          }))
        );
      }
      toggleSelect(columnId, 'parent', item.id);
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClickCapture={handleCtrlClick}
      sx={{
        display: 'flex',
        background: isItemSelected
          ? colors.selected
          : isActive
            ? colors.active
            : isDragging
              ? colors.active
              : colors.default,
        borderRadius: 1,
        border: 'none',
        borderLeft: `4px solid ${colors.border}`,
        transition: 'background 0.2s ease',
        opacity: isDragging || (isMultiDragActive && isItemSelected) ? 0 : 1,
        zIndex: isDragging ? 99999 : 'auto',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
        cursor: isFlat ? 'default' : 'pointer'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pl: isReadOnly ? 1 : 0 }}>
        {!isReadOnly && (
          <DragIndicatorIcon
            {...listeners}
            {...attributes}
            sx={{
              cursor: isDisabledDrag ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
              color: colors.border,
              opacity: isDisabledDrag ? 0.3 : 1,
              p: 1,
              '&:focus': { outline: 'none' }
            }}
          />
        )}
        {isFlat ? (
          // Mode flat : carte simple sans accordion
          <Box sx={{ px: 1, py: 1.5, flexGrow: 1 }}>
            <Typography
              variant="overline"
              sx={{
                textTransform: 'none',
                lineHeight: 1.3,
                color: isDark ? 'rgba(255,255,255,0.87)' : 'inherit'
              }}
            >
              {item.label || `ID ${item.id}`}
            </Typography>
          </Box>
        ) : (
          // Mode hiérarchique : accordion avec enfants
          <Accordion
            expanded={isExpanded}
            onChange={() => onToggleAccordion(item.label)}
            square={false}
            variant="outlined"
            sx={{
              width: '100%',
              background: 'inherit',
              border: 'none',
              boxShadow: 'none',
              '&.MuiAccordion-root:before': { display: 'none' }
            }}
            disableGutters
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                variant="overline"
                sx={{
                  textTransform: 'none',
                  lineHeight: 1.3,
                  color: isDark ? 'rgba(255,255,255,0.87)' : 'inherit'
                }}
              >
                {item.label || `ID ${item.id}`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              ref={accordionDetailsRef}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {isExpanded &&
                item.children.map((child) => (
                  <DndChildCard key={child.id} child={child} parent={item} columnId={columnId} />
                ))}
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Box>
  );
};

export default DndItemCard;
